/**
 * The unknown displays tab: which rows are ticked, and the CSV it downloads. Unknown displays are
 * frames seen in the play logs that match no screen on the campaign.
 */

type Row = { id: number | string };

/** Ticks a row, or un-ticks it if it was ticked. */
export const toggleUnknownSelection = <Id>(selected: Id[], rowId: Id): Id[] =>
    selected.includes(rowId) ? selected.filter(id => id !== rowId) : [...selected, rowId];

/** The header checkbox: un-ticks everything when everything is ticked, otherwise ticks every row. */
export const toggleAllUnknowns = (selected: Row['id'][], rows: Row[]): Row['id'][] =>
    selected.length === rows.length ? [] : rows.map(row => row.id);

/** The header checkbox's state. */
export const selectAllState = (selectedCount: number, rowCount: number) => ({
    checked: selectedCount === rowCount && rowCount > 0,
    indeterminate: selectedCount > 0 && selectedCount < rowCount,
});

/**
 * The downloaded CSV. Starts with a byte-order mark so Excel reads it as UTF-8 (frame ids and names
 * can carry accents). `fixHeader` corrects the API's misspelt first header.
 */
export const csvStringToBlob = (
    csvString: unknown,
    contentType = 'text/csv;charset=utf-8;',
    fixHeader = false,
) => {
    let out = String(csvString ?? '');
    if (fixHeader) {
        out = out.replace(/^idendtifier,identifier_type/, 'identifier,identifier_type');
    }
    return new Blob([`\uFEFF${out}`], { type: contentType });
};

/**
 * The download's MIME type: the response's `content-type` when it is a non-empty string, else CSV.
 * axios 1 types header values loosely (string, number, array, AxiosHeaders).
 */
export const downloadContentType = (header: unknown): string =>
    typeof header === 'string' && header !== '' ? header : 'text/csv;charset=utf-8;';
